import Email from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import SmartLabel from "@/domain/enums/SmartLabel";
import { Tone } from "@/hooks/useGenerateAnswerWithIA";
import useGetNavbarData from "@/hooks/useGetNavbarData";
import useMailActions from "@/hooks/useMailActions";
import { isSameCopy } from "@/lib/mailIdentity";
import mailStore, { setOpenedMail } from "@/stores/mailStore";
import { useSelector } from "@tanstack/react-store";
import { Editor } from "@tiptap/react";
import {
    Dispatch,
    RefObject,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from "react";
import CenteredSpinner from "../CenteredSpinner";
import { FocusTarget } from "../mailsList/MailAction";
import { ContextBarVariant } from "../mailThread/ContextBar";
import MailThread from "../mailThread/MailThread";
import Spinner from "../ui/spinner";
import DeepFocusNavigationBar, {
    DeepFocusNavigationBarDirection,
} from "./DeepFocusNavigationBar";
import { mergeDeepFocusOrder, openDeepFocusOrder } from "./deepFocusOrder";

const INFINITE_SCROLL_THRESHOLD = 5;

export interface DeepFocusViewProps {
    mails: Array<Email>;
    loadNextPage: () => void;
    isLoading?: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    editor: Editor;
    mailInputExpanded: boolean;
    setMailInputExpanded: (
        inputExpanded: boolean,
        focusTarget?: FocusTarget,
    ) => void;
    toInputRef: RefObject<HTMLInputElement | null>;
    attachments: EmailAttachment[];
    setAttachments: Dispatch<SetStateAction<EmailAttachment[]>>;
    acceptOrRefuseSuggestion: (accept: boolean) => void;
    isLoadingSuggestion: boolean;
    isPendingChange: boolean;
    makeSuggestion: (action: Tone) => void;
    pendingSuggestion: boolean;
    setPendingSuggestion: Dispatch<SetStateAction<boolean>>;
    suggestionEditor: Editor;
}

const DeepFocusView = ({
    mails,
    loadNextPage,
    isLoadingSuggestion,
    hasNextPage,
    isFetchingNextPage,
    editor,
    mailInputExpanded,
    setMailInputExpanded,
    toInputRef,
    attachments,
    setAttachments,
    acceptOrRefuseSuggestion,
    isLoading,
    isPendingChange,
    makeSuggestion,
    pendingSuggestion,
    setPendingSuggestion,
    suggestionEditor,
}: DeepFocusViewProps) => {
    const { unProcessedFocusMailCount } = useGetNavbarData();
    const { setProcessStatus } = useMailActions();

    // La liste vivante du Focus change pendant la lecture : on garde l'ordre pris à l'ouverture
    const [deepFocusOrder, setDeepFocusOrder] = useState(() =>
        openDeepFocusOrder(mails),
    );
    const [mergedLiveMails, setMergedLiveMails] = useState(mails);
    if (mergedLiveMails !== mails) {
        setMergedLiveMails(mails);
        setDeepFocusOrder(mergeDeepFocusOrder(deepFocusOrder, mails));
    }

    const mailById = useMemo(
        () => new Map(mails.map((mail) => [mail.uniqueMailvistaId, mail])),
        [mails],
    );
    const { mailIds, displayedMailId } = deepFocusOrder;
    const displayedMailIndex = Math.max(
        displayedMailId ? mailIds.indexOf(displayedMailId) : 0,
        0,
    );
    const currentMail = displayedMailId
        ? mailById.get(displayedMailId)
        : undefined;

    const handleMailChange = (direction: DeepFocusNavigationBarDirection) => {
        if (mailIds.length === 0) return;

        const targetIndex =
            direction === DeepFocusNavigationBarDirection.LEFT
                ? (displayedMailIndex - 1 + mailIds.length) % mailIds.length
                : (displayedMailIndex + 1) % mailIds.length;
        const targetMailId = mailIds[targetIndex];
        setDeepFocusOrder({ mailIds, displayedMailId: targetMailId });
    };

    // Les actions sur les mails ouvrent le suivant de la liste vivante, pas celui de l'ordre du Deep Focus
    const openedMail = useSelector(mailStore, (state) => state.openedMail);
    // Plus de mail ouvert = dernier mail traité : ne pas le rouvrir avant que la liste vide ferme le Deep Focus
    useEffect(() => {
        if (currentMail && openedMail && !isSameCopy(openedMail, currentMail)) {
            setOpenedMail(currentMail, { openIntent: false });
        }
    }, [currentMail, openedMail]);

    // Un nouvel objet pour le même mail pré-remplit à nouveau la réponse et efface le brouillon
    const threadMail =
        currentMail && openedMail && isSameCopy(openedMail, currentMail)
            ? openedMail
            : currentMail;

    const handleProcessingMail = () => {
        if (currentMail) {
            setProcessStatus.mutateAsync({
                mail: currentMail,
                isUserProcessed: !currentMail.isUserProcessed,
                listId: SmartLabel.Focus,
            });
        }
    };

    useEffect(() => {
        if (mailIds.length === 0 || !unProcessedFocusMailCount) {
            return;
        }

        if (
            hasNextPage &&
            !isFetchingNextPage &&
            mailIds.length - displayedMailIndex < INFINITE_SCROLL_THRESHOLD
        ) {
            loadNextPage();
        }
    }, [
        unProcessedFocusMailCount,
        displayedMailIndex,
        hasNextPage,
        isFetchingNextPage,
        mailIds.length,
        loadNextPage,
    ]);

    if (!unProcessedFocusMailCount) {
        return <CenteredSpinner size="large" />;
    }

    if (currentMail) {
        return (
            <div className="relative h-full w-full">
                {unProcessedFocusMailCount > 0 && (
                    <MailThread
                        acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
                        attachments={attachments}
                        classname="absolute bottom-0 left-0 right-0 top-0 px-11 pb-30 pt-24"
                        editor={editor}
                        handleProcessingFocusMail={handleProcessingMail}
                        isLoading={isLoadingSuggestion}
                        isPendingChange={isPendingChange}
                        listId={SmartLabel.Focus}
                        mail={threadMail}
                        mailInputExpanded={mailInputExpanded}
                        makeSuggestion={makeSuggestion}
                        pendingSuggestion={pendingSuggestion}
                        setAttachments={setAttachments}
                        setMailInputExpanded={setMailInputExpanded}
                        setPendingSuggestion={setPendingSuggestion}
                        suggestionEditor={suggestionEditor}
                        toInputRef={toInputRef}
                        variant={ContextBarVariant.DEEP_FOCUS}
                    />
                )}
                <div className="absolute bottom-0 left-0 right-0 h-19 border-t border-white/10 bg-white/10 px-11 py-3 backdrop-blur-md">
                    {isLoading && (
                        <Spinner className="absolute right-4 top-1/2 -translate-y-1/2" />
                    )}
                    <DeepFocusNavigationBar
                        displayedMailIndex={displayedMailIndex}
                        handleMailChange={handleMailChange}
                        handleProcessingMail={handleProcessingMail}
                        unprocessedMailCount={unProcessedFocusMailCount}
                    />
                </div>
            </div>
        );
    }

    return <CenteredSpinner size="large" />;
};

export default DeepFocusView;
